import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  User,
  Lock,
  Bell,
  Palette,
  Shield,
  Eye,
  EyeOff,
  Check,
  Sun,
  Moon,
  Monitor,
  KeyRound,
  RefreshCw,
  CheckCircle2,
  AlertCircle,
  Copy,
} from 'lucide-react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  Button,
  Input,
  Avatar,
  Badge,
  Textarea,
} from '../components/ui';
import { usersApi, claudeApi } from '../lib/api';
import { formatDateTime } from '../lib/utils';
import { useAuthStore } from '../stores/authStore';
import { useThemeStore } from '../stores/themeStore';
import toast from 'react-hot-toast';

const profileSchema = z.object({
  first_name: z.string().min(1, 'First name is required'),
  last_name: z.string().min(1, 'Last name is required'),
});

const passwordSchema = z
  .object({
    current_password: z.string().min(8, 'Password must be at least 8 characters'),
    new_password: z
      .string()
      .min(8, 'Password must be at least 8 characters')
      .regex(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
        'Password must contain uppercase, lowercase, and number'
      ),
    confirm_password: z.string(),
  })
  .refine((data) => data.new_password === data.confirm_password, {
    message: "Passwords don't match",
    path: ['confirm_password'],
  });

type ProfileForm = z.infer<typeof profileSchema>;
type PasswordForm = z.infer<typeof passwordSchema>;

const themeOptions = [
  { value: 'light', label: 'Light', icon: Sun, description: 'Always use light mode' },
  { value: 'dark', label: 'Dark', icon: Moon, description: 'Always use dark mode' },
  { value: 'system', label: 'System', icon: Monitor, description: 'Follow system preferences' },
] as const;

export const Settings: React.FC = () => {
  const { user, updateUser } = useAuthStore();
  const { theme, setTheme } = useThemeStore();
  const [activeTab, setActiveTab] = useState('profile');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [credentialsJson, setCredentialsJson] = useState('');
  const isAdmin = user?.role === 'admin';
  const queryClient = useQueryClient();

  const profileForm = useForm<ProfileForm>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      first_name: user?.first_name || '',
      last_name: user?.last_name || '',
    },
  });

  const passwordForm = useForm<PasswordForm>({
    resolver: zodResolver(passwordSchema),
  });

  const profileMutation = useMutation({
    mutationFn: usersApi.updateProfile,
    onSuccess: (data) => {
      updateUser(data);
      toast.success('Profile updated successfully!');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    },
  });

  const passwordMutation = useMutation({
    mutationFn: (data: { current_password: string; new_password: string }) =>
      usersApi.updatePassword(data),
    onSuccess: () => {
      passwordForm.reset();
      toast.success('Password updated successfully!');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to update password');
    },
  });

  const handleProfileSubmit = (data: ProfileForm) => {
    profileMutation.mutate(data);
  };

  const handlePasswordSubmit = (data: PasswordForm) => {
    passwordMutation.mutate({
      current_password: data.current_password,
      new_password: data.new_password,
    });
  };

  // ── Claude connection ────────────────────────────────────────────────────
  const { data: claudeStatus, isLoading: claudeLoading } = useQuery({
    queryKey: ['claude-status'],
    queryFn: () => claudeApi.getStatus(),
    enabled: isAdmin,
    staleTime: 0,
  });

  const uploadCredentialsMutation = useMutation({
    mutationFn: (json: string) => claudeApi.uploadCredentials(json),
    onSuccess: (data) => {
      setCredentialsJson('');
      queryClient.invalidateQueries({ queryKey: ['claude-status'] });
      toast.success(data.message);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to save credentials');
    },
  });

  const validateMutation = useMutation({
    mutationFn: () => claudeApi.validate(),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['claude-status'] });
      if (data.ok) toast.success(data.message);
      else toast.error(data.message);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Could not reach the Claude service');
    },
  });

  const claudeStateBadge = {
    active: { variant: 'success' as const, label: 'Connected' },
    expiring_soon: { variant: 'warning' as const, label: 'Expiring soon' },
    expired: { variant: 'danger' as const, label: 'Expired' },
    not_configured: { variant: 'default' as const, label: 'Not configured' },
  };

  const tabs = [
    { id: 'profile', label: 'Profile', icon: User },
    { id: 'security', label: 'Security', icon: Lock },
    { id: 'appearance', label: 'Appearance', icon: Palette },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    // Managing the shared Claude credential is an admin concern.
    ...(isAdmin ? [{ id: 'claude', label: 'Claude Connection', icon: KeyRound }] : []),
  ];

  const userName = user
    ? `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email
    : '';

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Settings</h1>
        <p className="text-gray-500 dark:text-gray-400">
          Manage your account settings and preferences
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Sidebar */}
        <Card className="lg:col-span-1 h-fit">
          <CardContent className="p-2">
            <nav className="space-y-1">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-3 w-full px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                    activeTab === tab.id
                      ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400'
                      : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
                  }`}
                >
                  <tab.icon className="h-5 w-5" />
                  {tab.label}
                </button>
              ))}
            </nav>
          </CardContent>
        </Card>

        {/* Content */}
        <div className="lg:col-span-3 space-y-6">
          {activeTab === 'profile' && (
            <>
              {/* Profile Card */}
              <Card>
                <CardHeader>
                  <CardTitle>Profile Information</CardTitle>
                  <CardDescription>
                    Update your personal information and profile picture
                  </CardDescription>
                </CardHeader>
                <CardContent className="p-6 pt-0">
                  <div className="flex items-center gap-6 mb-8">
                    <Avatar name={userName} size="xl" />
                    <div>
                      <h3 className="font-semibold text-gray-900 dark:text-white">
                        {userName}
                      </h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400">{user?.email}</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400 capitalize mt-1">
                        <Shield className="h-3.5 w-3.5 inline mr-1" />
                        {user?.role}
                      </p>
                    </div>
                  </div>

                  <form onSubmit={profileForm.handleSubmit(handleProfileSubmit)} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <Input
                        label="First Name"
                        {...profileForm.register('first_name')}
                        error={profileForm.formState.errors.first_name?.message}
                      />
                      <Input
                        label="Last Name"
                        {...profileForm.register('last_name')}
                        error={profileForm.formState.errors.last_name?.message}
                      />
                    </div>
                    <Input label="Email" value={user?.email || ''} disabled />
                    <div className="flex justify-end">
                      <Button type="submit" isLoading={profileMutation.isPending}>
                        Save Changes
                      </Button>
                    </div>
                  </form>
                </CardContent>
              </Card>
            </>
          )}

          {activeTab === 'security' && (
            <Card>
              <CardHeader>
                <CardTitle>Change Password</CardTitle>
                <CardDescription>
                  Update your password to keep your account secure
                </CardDescription>
              </CardHeader>
              <CardContent className="p-6 pt-0">
                <form
                  onSubmit={passwordForm.handleSubmit(handlePasswordSubmit)}
                  className="space-y-4 max-w-md"
                >
                  <Input
                    label="Current Password"
                    type={showCurrentPassword ? 'text' : 'password'}
                    {...passwordForm.register('current_password')}
                    error={passwordForm.formState.errors.current_password?.message}
                    rightIcon={
                      <button
                        type="button"
                        onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      >
                        {showCurrentPassword ? (
                          <EyeOff className="h-5 w-5" />
                        ) : (
                          <Eye className="h-5 w-5" />
                        )}
                      </button>
                    }
                  />
                  <Input
                    label="New Password"
                    type={showNewPassword ? 'text' : 'password'}
                    {...passwordForm.register('new_password')}
                    error={passwordForm.formState.errors.new_password?.message}
                    rightIcon={
                      <button
                        type="button"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                      >
                        {showNewPassword ? (
                          <EyeOff className="h-5 w-5" />
                        ) : (
                          <Eye className="h-5 w-5" />
                        )}
                      </button>
                    }
                  />
                  <Input
                    label="Confirm New Password"
                    type="password"
                    {...passwordForm.register('confirm_password')}
                    error={passwordForm.formState.errors.confirm_password?.message}
                  />
                  <div className="flex justify-end pt-4">
                    <Button type="submit" isLoading={passwordMutation.isPending}>
                      Update Password
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          )}

          {activeTab === 'appearance' && (
            <Card>
              <CardHeader>
                <CardTitle>Appearance</CardTitle>
                <CardDescription>
                  Customize how the application looks on your device
                </CardDescription>
              </CardHeader>
              <CardContent className="p-6 pt-0">
                <div className="space-y-4">
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Theme</p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {themeOptions.map((option) => (
                      <button
                        key={option.value}
                        onClick={() => setTheme(option.value)}
                        className={`relative flex flex-col items-center p-4 rounded-xl border-2 transition-all ${
                          theme === option.value
                            ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                            : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                        }`}
                      >
                        {theme === option.value && (
                          <div className="absolute top-2 right-2">
                            <Check className="h-4 w-4 text-primary-600" />
                          </div>
                        )}
                        <div
                          className={`p-3 rounded-full mb-3 ${
                            theme === option.value
                              ? 'bg-primary-100 dark:bg-primary-900/30'
                              : 'bg-gray-100 dark:bg-gray-800'
                          }`}
                        >
                          <option.icon
                            className={`h-6 w-6 ${
                              theme === option.value
                                ? 'text-primary-600'
                                : 'text-gray-600 dark:text-gray-400'
                            }`}
                          />
                        </div>
                        <span className="font-medium text-gray-900 dark:text-white">
                          {option.label}
                        </span>
                        <span className="text-xs text-gray-500 dark:text-gray-400 mt-1 text-center">
                          {option.description}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {activeTab === 'notifications' && (
            <Card>
              <CardHeader>
                <CardTitle>Notification Preferences</CardTitle>
                <CardDescription>
                  Control how and when you receive notifications
                </CardDescription>
              </CardHeader>
              <CardContent className="p-6 pt-0">
                <div className="space-y-6">
                  <div className="flex items-center justify-between py-4 border-b border-gray-200 dark:border-gray-700">
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">
                        Email Notifications
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Receive email updates about your blog generation
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input type="checkbox" className="sr-only peer" defaultChecked />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 dark:peer-focus:ring-primary-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-primary-600"></div>
                    </label>
                  </div>
                  <div className="flex items-center justify-between py-4 border-b border-gray-200 dark:border-gray-700">
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">
                        Blog Completion Alerts
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Get notified when your blog is ready
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input type="checkbox" className="sr-only peer" defaultChecked />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 dark:peer-focus:ring-primary-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-primary-600"></div>
                    </label>
                  </div>
                  <div className="flex items-center justify-between py-4">
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">
                        Marketing Updates
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Receive news and feature updates
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input type="checkbox" className="sr-only peer" />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 dark:peer-focus:ring-primary-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-primary-600"></div>
                    </label>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {activeTab === 'claude' && isAdmin && (
            <>
              <Card>
                <CardHeader>
                  <CardTitle>Claude Connection</CardTitle>
                  <CardDescription>
                    Blog generation runs on a shared Claude credential. It renews itself
                    automatically each time it is used, so this rarely needs attention.
                  </CardDescription>
                </CardHeader>
                <CardContent className="p-6 pt-0">
                  {claudeLoading ? (
                    <p className="text-sm text-gray-500 dark:text-gray-400">Checking status...</p>
                  ) : claudeStatus ? (
                    <div className="space-y-4">
                      <div className="flex items-center gap-3">
                        <Badge variant={claudeStateBadge[claudeStatus.state].variant}>
                          {claudeStateBadge[claudeStatus.state].label}
                        </Badge>
                        {claudeStatus.account_email && (
                          <span className="text-sm text-gray-600 dark:text-gray-300">
                            connected as {claudeStatus.account_email}
                          </span>
                        )}
                      </div>

                      {claudeStatus.state !== 'not_configured' && (
                        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                          <div>
                            <dt className="text-gray-500 dark:text-gray-400">Renews within</dt>
                            <dd className="font-medium text-gray-900 dark:text-white">
                              {claudeStatus.days_remaining !== null
                                ? `${Math.floor(claudeStatus.days_remaining)} days`
                                : '—'}
                            </dd>
                          </div>
                          <div>
                            <dt className="text-gray-500 dark:text-gray-400">Plan</dt>
                            <dd className="font-medium text-gray-900 dark:text-white">
                              {claudeStatus.subscription_type || '—'}
                            </dd>
                          </div>
                          <div>
                            <dt className="text-gray-500 dark:text-gray-400">Last renewed</dt>
                            <dd className="font-medium text-gray-900 dark:text-white">
                              {claudeStatus.last_synced_at
                                ? formatDateTime(claudeStatus.last_synced_at)
                                : '—'}
                            </dd>
                          </div>
                          <div>
                            <dt className="text-gray-500 dark:text-gray-400">Uploaded</dt>
                            <dd className="font-medium text-gray-900 dark:text-white">
                              {claudeStatus.uploaded_at
                                ? formatDateTime(claudeStatus.uploaded_at)
                                : '—'}
                            </dd>
                          </div>
                        </dl>
                      )}

                      {claudeStatus.message && (
                        <div
                          className={`flex items-start gap-2 px-3 py-2 rounded-lg text-sm ${
                            claudeStatus.state === 'active'
                              ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400'
                              : 'bg-yellow-50 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-400'
                          }`}
                        >
                          <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                          <span>{claudeStatus.message}</span>
                        </div>
                      )}

                      <div className="flex justify-end">
                        <Button
                          variant="outline"
                          leftIcon={<RefreshCw className="h-4 w-4" />}
                          isLoading={validateMutation.isPending}
                          onClick={() => validateMutation.mutate()}
                        >
                          Test connection
                        </Button>
                      </div>
                    </div>
                  ) : null}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Update credential</CardTitle>
                  <CardDescription>
                    Only needed if the connection has lapsed — for example after a long
                    period of no activity, or if access was revoked.
                  </CardDescription>
                </CardHeader>
                <CardContent className="p-6 pt-0">
                  <ol className="space-y-3 text-sm text-gray-600 dark:text-gray-300 mb-5">
                    <li className="flex gap-2">
                      <span className="font-semibold text-gray-900 dark:text-white">1.</span>
                      <span>
                        On a machine with Claude Code installed, run:
                        <span className="mt-1.5 flex items-center gap-2">
                          <code className="px-2 py-1 rounded bg-gray-100 dark:bg-gray-800 font-mono text-xs">
                            claude setup-token
                          </code>
                          <button
                            type="button"
                            onClick={() => {
                              navigator.clipboard.writeText('claude setup-token');
                              toast.success('Command copied to clipboard!');
                            }}
                            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                            aria-label="Copy command"
                          >
                            <Copy className="h-3.5 w-3.5" />
                          </button>
                        </span>
                      </span>
                    </li>
                    <li className="flex gap-2">
                      <span className="font-semibold text-gray-900 dark:text-white">2.</span>
                      <span>
                        Open the credentials file it writes —{' '}
                        <code className="px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-800 font-mono text-xs">
                          ~/.claude/.credentials.json
                        </code>
                      </span>
                    </li>
                    <li className="flex gap-2">
                      <span className="font-semibold text-gray-900 dark:text-white">3.</span>
                      <span>Paste its full contents below and save.</span>
                    </li>
                  </ol>

                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      uploadCredentialsMutation.mutate(credentialsJson);
                    }}
                    className="space-y-4"
                  >
                    <Textarea
                      label="Credentials JSON"
                      placeholder='{"claudeAiOauth": { ... }}'
                      value={credentialsJson}
                      onChange={(e) => setCredentialsJson(e.target.value)}
                      className="font-mono text-xs min-h-[160px]"
                      helperText="Stored encrypted. The token is never displayed or logged."
                    />
                    <div className="flex justify-end gap-3">
                      <Button
                        type="submit"
                        leftIcon={<CheckCircle2 className="h-4 w-4" />}
                        isLoading={uploadCredentialsMutation.isPending}
                        disabled={!credentialsJson.trim()}
                      >
                        Validate & Save
                      </Button>
                    </div>
                  </form>
                </CardContent>
              </Card>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
